import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  actionOpenJobCloseAtDialog,
  actionCloseJobCloseAtDialog,
} from 'lib/actions';
import { JobCloseAtDialogEventEmitter } from 'components/JobCloseAtDialog';
import { getJobTimePromptEnabled } from 'lib/selectors/getJobTimePromptEnabled';
import moment from 'moment';

/**
 * Custom hook to manage the job end date selection dialog
 * @returns {Function} A function that returns a Promise resolving with the selected date or rejecting if cancelled
 */
const useRequestJobCloseAt = () => {
  const dispatch = useDispatch();

  const isJobTimePromptEnabled = useSelector(getJobTimePromptEnabled);

  /**
   * Opens the job end date selection dialog
   * @returns {Object} The dispatched action
   */
  const openJobCloseAtDialog = useCallback(() => {
    return dispatch(actionOpenJobCloseAtDialog());
  }, [dispatch]);

  /**
   * Closes the job end date selection dialog
   * @returns {Object} The dispatched action
   */
  const closeJobCloseAtDialog = useCallback(() => {
    return dispatch(actionCloseJobCloseAtDialog());
  }, [dispatch]);

  /**
   * Request job end date selection
   * @returns {Promise<string>} Promise that resolves with the selected date or rejects if cancelled
   */
  return useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!isJobTimePromptEnabled) {
        const nowISO = moment().toISOString();
        resolve(nowISO);
        return;
      }

      JobCloseAtDialogEventEmitter.init();

      JobCloseAtDialogEventEmitter.on('success', (selectedDate) => {
        closeJobCloseAtDialog();
        resolve(selectedDate);
      });

      JobCloseAtDialogEventEmitter.on('cancel', () => {
        closeJobCloseAtDialog();
        reject(new Error('Job end time selection was cancelled'));
      });

      openJobCloseAtDialog();
    });
  }, [openJobCloseAtDialog, closeJobCloseAtDialog, isJobTimePromptEnabled]);
};

export default useRequestJobCloseAt;
